import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

// Identity cell: a colored initial avatar beside a name (and an
// optional secondary line). Shared so any table's "who" column — users,
// drivers, later fleet operators — reads the same instead of each page
// hand-rolling an avatar. The avatar color is derived deterministically from
// the key so the same person keeps the same color across renders.
const AVATAR_COLORS = ['#6C7BF0', '#E8833A', '#3DAE7C', '#D8617E', '#4C9BE8', '#B57BE0', '#E0A93A', '#5AB0A8'];

// Cache the derived color per key so it isn't re-hashed on every cell render.
const colorCache = new Map();
const colorFor = (key = '') => {
  if (colorCache.has(key)) return colorCache.get(key);
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  const color = AVATAR_COLORS[hash % AVATAR_COLORS.length];
  colorCache.set(key, color);
  return color;
};

const initialOf = (name = '') => {
  const trimmed = String(name).trim();
  return trimmed ? trimmed[0].toUpperCase() : '?';
};

// Exported so surfaces that need the avatar at a different size, or beside
// something other than a plain name, get the same color and initial rather than
// re-deriving them and drifting.
export { colorFor as avatarColorFor, initialOf as avatarInitialOf };

// The avatar on its own. `photoUrl` wins when present; the colored initial is the
// fallback, so a driver with a photo and one without still occupy the same slot.
export const Avatar = ({ name, photoUrl, colorKey, size = 28 }) => (
  <Box
    sx={{
      width: size,
      height: size,
      borderRadius: '50%',
      flexShrink: 0,
      overflow: 'hidden',
      backgroundColor: colorFor(colorKey || name || ''),
      color: '#fff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: `${Math.round(size * 0.43)}px`,
      fontWeight: 600,
    }}
  >
    {photoUrl ? (
      <Box component="img" src={photoUrl} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
    ) : (
      initialOf(name)
    )}
  </Box>
);

const IdentityCell = ({ name, secondary, colorKey, photoUrl, size = 28 }) => {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
      <Avatar name={name} photoUrl={photoUrl} colorKey={colorKey} size={size} />
      <Box sx={{ minWidth: 0 }}>
        <Typography noWrap sx={{ fontSize: '14px', fontWeight: 600, lineHeight: '18px' }}>
          {name}
        </Typography>
        {secondary && (
          <Typography noWrap sx={{ fontSize: '12.5px', color: 'text.secondary', lineHeight: '16px' }}>
            {secondary}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default IdentityCell;
